import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    InputAdornment,
    CircularProgress,
    Alert,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { checkValidString } from '../../../utils/helpers';
import RequestDetails from '../../../components/Dialog/RequestDetails';
import Translation from '../../../utils/Translation';

export default function TrackShipmentNew() {
    const theme = useTheme();
    const { t } = useTranslation();
    const dynamicState = useSelector((state) => state.dynamicConfig);

    const [trackNumber, setTrackNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(false);

    const handleSubmit = async (e) => {
        e?.preventDefault();

        if (!checkValidString(trackNumber)) {
            setError(t('home.track-error'));
            return;
        }

        setError('');
        setLoading(true);

        try {
            const apiResult = await axios.get(
                `${dynamicState.prodApiServerBaseUrl}v1/get-single-request-by-reference-code-route/${trackNumber}`
            );
            setResult(apiResult.data);
            setOpen(true);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Unable to find shipment');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    return (
        <>
            {open && result?.customer_ref && (
                <RequestDetails open={open} data={result} handleClose={handleClose} />
            )}

            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    maxWidth: 500,
                }}
            >
                <TextField
                    fullWidth
                    placeholder={t('home.type-your-order')}
                    value={trackNumber}
                    onChange={(e) => {
                        setTrackNumber(e.target.value);
                        setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    error={!!error}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                                borderColor: theme.palette.secondary.main,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.secondary.main,
                            },
                        },
                    }}
                />

                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                        minWidth: { xs: '100%', sm: 120 },
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: theme.palette.secondary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        <Translation data="home.track" />
                    )}
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mt: 2, maxWidth: 500, borderRadius: 2 }}
                    onClose={() => setError('')}
                >
                    {error}
                </Alert>
            )}
        </>
    );
}
